import { useCallback, useEffect, useRef, useState } from 'react';

// Reads/writes the active tab to the URL as `?tab=<key>` inside the hash
// route (e.g. `#/receive-payments?tab=invoices`). The path portion is
// untouched so the existing hash-based router in `app.tsx` is unaffected.
//
// Writes use `history.replaceState` so clicking through tabs doesn't pollute
// the back-stack. Browser back/forward between *pages* still works because
// page navigation goes through `window.location.hash =` elsewhere.

const TAB_PARAM = 'tab';

function splitHash(hash: string): { path: string; query: string } {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const qIdx = raw.indexOf('?');
  if (qIdx < 0) return { path: raw || '/', query: '' };
  return { path: raw.slice(0, qIdx) || '/', query: raw.slice(qIdx + 1) };
}

function readTabFromHash(param: string): string | null {
  const { query } = splitHash(window.location.hash);
  if (!query) return null;
  return new URLSearchParams(query).get(param);
}

function writeTabToHash(tab: string, param: string) {
  const { path, query } = splitHash(window.location.hash);
  const params = new URLSearchParams(query);
  if (params.get(param) === tab) return;
  params.set(param, tab);
  const nextHash = `#${path}?${params.toString()}`;
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}${nextHash}`);
}

function clearHashParam(param: string) {
  const { path, query } = splitHash(window.location.hash);
  const params = new URLSearchParams(query);
  if (!params.has(param)) return;
  params.delete(param);
  const qs = params.toString();
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}#${path}${qs ? `?${qs}` : ''}`);
}

/**
 * [param] names the query key, defaulting to `tab`. A page that nests one tab
 * strip inside another passes its own key (e.g. `section`) so the two do not
 * fight over `?tab=`, and both levels stay deep-linkable —
 * `#/pos-management?tab=pos-config&section=config`.
 */
export function usePersistentTab<T extends string>(
  validTabs: readonly T[],
  defaultTab: T,
  param: string = TAB_PARAM,
): [T, (tab: T) => void] {
  // Keep latest validTabs in a ref so the hashchange handler always sees
  // the current set (feature-flag-gated tabs load asynchronously).
  const validRef = useRef(validTabs);
  validRef.current = validTabs;

  const resolve = useCallback((): T => {
    const v = readTabFromHash(param);
    if (v && (validRef.current as readonly string[]).includes(v)) return v as T;
    return defaultTab;
  }, [defaultTab, param]);

  const [activeTab, setActiveTabState] = useState<T>(resolve);

  // Mirror the resolved tab into the URL on mount so deep-linking,
  // refreshes, and the default state all converge on a single URL form.
  useEffect(() => {
    writeTabToHash(activeTab, param);
    // mount-only on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back/forward navigation between hash entries (e.g. user navigated
  // away and came back, or hit Back to a different tab).
  useEffect(() => {
    const onHashChange = () => {
      const next = resolve();
      setActiveTabState((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [resolve]);

  const setActiveTab = useCallback(
    (tab: T) => {
      setActiveTabState(tab);
      writeTabToHash(tab, param);
    },
    [param],
  );

  return [activeTab, setActiveTab];
}

/**
 * A single hash query param that isn't a fixed tab set — for deep links whose
 * value is open-ended, like the reports page's selected report id
 * (`#/reports?tab=pos&report=total-daily-department-sales`).
 *
 * Unlike usePersistentTab this writes nothing on mount and removes the param
 * entirely when set to null, so the plain page URL stays clean and a Back out
 * of a drill-down doesn't leave a stale deep link behind to reopen on refresh.
 */
export function useHashParam(param: string): [string | null, (value: string | null) => void] {
  const read = useCallback(() => readTabFromHash(param), [param]);
  const [value, setValue] = useState<string | null>(read);

  useEffect(() => {
    const onHashChange = () => {
      const next = read();
      setValue((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [read]);

  const update = useCallback(
    (next: string | null) => {
      setValue(next);
      if (next === null) clearHashParam(param);
      else writeTabToHash(next, param);
    },
    [param],
  );

  return [value, update];
}
